import React from "react";
import {
    IconButton,
    InputAdornment,
    TextField,
} from "@mui/material";
import { Close, Search } from "@mui/icons-material";

export interface DebouncedSearchInputProps {
    /** Current search input value */
    value: string;
    /** Callback when input changes */
    onChange: (value: string) => void;
    /** Placeholder text */
    placeholder?: string;
    /** Disabled state */
    disabled?: boolean;
}

/**
 * Search input component with clear button.
 * The debouncing logic is handled externally via useListState hook.
 */
const DebouncedSearchInput: React.FC<DebouncedSearchInputProps> = ({
    value,
    onChange,
    placeholder = "Search...",
    disabled = false,
}) => {
    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        onChange(event.target.value);
    };

    const handleClear = () => {
        onChange("");
    };

    const showClearButton = value.length > 0;

    return (
        <TextField
            value={value}
            onChange={handleChange}
            placeholder={placeholder}
            disabled={disabled}
            size="small"
            fullWidth
            InputProps={{
                startAdornment: (
                    <InputAdornment position="start">
                        <Search fontSize="small" sx={{ color: "#7498A6" }} />
                    </InputAdornment>
                ),
                endAdornment: showClearButton && (
                    <InputAdornment position="end">
                        <IconButton
                            size="small"
                            onClick={handleClear}
                            sx={{ color: "#7498A6" }}
                            aria-label="Clear search"
                        >
                            <Close fontSize="small" />
                        </IconButton>
                    </InputAdornment>
                ),
            }}
            sx={{
                "& .MuiOutlinedInput-root": {
                    backgroundColor: "#fff",
                    "&:hover .MuiOutlinedInput-notchedOutline": {
                        borderColor: "#7498A6",
                    },
                    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                        borderColor: "#7498A6",
                    },
                },
            }}
        />
    );
};

export default DebouncedSearchInput;
